import React from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import CfriShell from "./CfriShell";
import { SourceLine, fmt } from "./CfriUI";
import { SectionCard } from "../funder/FunderUI";

/**
 * "Before the meeting": the one-page committee briefing for one proposal, for
 * committee members who are not scientists. Prints on its own (the portal
 * chrome is hidden in print).
 */
export default function CfriBriefing() {
    const { id } = useParams();
    return (
        <CfriShell active={id}>
            {(content) => <Briefing content={content} id={id} />}
        </CfriShell>
    );
}

function Section({ section, children, className }) {
    return (
        <SectionCard kicker={section.heading} className={className}>
            <div style={{ marginBottom: 10 }}><SourceLine source={section.source} /></div>
            {children}
            {section.note && <p className="cfri-section-note">{section.note}</p>}
        </SectionCard>
    );
}

function Briefing({ content, id }) {
    const navigate = useNavigate();
    const p = content.proposals.find((x) => x.id === id);
    if (!p) return <Navigate to="/cfri" replace />;
    const b = p.briefing;
    const maxPapers = Math.max(...b.whereItSits.areas.map((a) => a.papers), 1);

    return (
        <div className="funder-section-stack cfri-briefing">
            <div className="cfri-no-print" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span className="funder-link" onClick={() => navigate(`/cfri/proposal/${p.id}`)}><ArrowLeft size={14} /> Back to the proposal</span>
                <button className="funder-btn-ghost" style={{ marginLeft: "auto" }} onClick={() => window.print()}>
                    <Printer size={14} /> Print
                </button>
            </div>

            <div className="funder-page-header">
                <div className="funder-overline">Before the meeting · committee briefing</div>
                <h1 className="funder-h1">{p.title}</h1>
                <p className="funder-subtitle" style={{ margin: 0 }}>
                    {p.applicant}{p.institution ? ` · ${p.institution}` : ""} · {p.program}, submitted {p.submitted}
                </p>
                {b.intro && <p className="funder-subtitle" style={{ margin: "10px 0 0", color: "#8fb3a6" }}>{b.intro}</p>}
            </div>

            <Section section={b.summary}>
                <blockquote className="cfri-quote">{b.summary.text}</blockquote>
            </Section>

            <div className="funder-grid-2 cfri-briefing-pair">
                <Section section={b.whereItSits}>
                    <div className="cfri-bars">
                        {b.whereItSits.areas.map((a) => (
                            <div key={a.area} className="cfri-bar-row" title={`${a.area}: ${fmt(a.papers)} papers`}>
                                <div className="cfri-bar-label">{a.area}</div>
                                <div className="cfri-bar-line">
                                    <div className="cfri-bar" style={{ width: `${(a.papers / maxPapers) * 100}%` }} />
                                    <span className="cfri-bar-value">{fmt(a.papers)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>

                <Section section={b.closest}>
                    <ol className="cfri-paper-list">
                        {b.closest.papers.map((paper) => (
                            <li key={paper.url}>
                                <a className="cfri-paper-link" href={paper.url} target="_blank" rel="noopener noreferrer">{paper.title}</a>
                                <span className="cfri-muted"> ({paper.year})</span>
                            </li>
                        ))}
                    </ol>
                </Section>
            </div>

            <Section section={b.people}>
                <div className="cfri-stack-wrap">
                    <table className="funder-table cfri-table cfri-stack">
                        <thead><tr><th>Researcher</th><th>Institution</th><th>Recent paper</th></tr></thead>
                        <tbody>
                            {b.people.people.map((person) => (
                                <tr key={person.name}>
                                    <td style={{ fontWeight: 600, color: "var(--ink-900)" }}>{person.name}</td>
                                    <td className="cfri-stack-line">{person.institution}</td>
                                    <td className="cfri-stack-line">{person.paper}{person.year ? <span className="cfri-muted"> ({person.year})</span> : null}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            <Section section={b.numbers}>
                {b.numbers.rows.length > 0 && (
                    <div className="cfri-defs">
                        {b.numbers.rows.map((row, i) => (
                            <div key={i} className="cfri-def">
                                <div className="cfri-def-term">{row.values}</div>
                                <div className="cfri-def-body">“{row.quote}”</div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            <Section section={b.terms}>
                <div className="cfri-defs">
                    {b.terms.rows.map((row) => (
                        <div key={row.term} className="cfri-def">
                            <div className="cfri-def-term">{row.term}</div>
                            <div className="cfri-def-body">
                                <div style={{ fontWeight: 600, color: "var(--ink-800)" }}>{row.expansion}</div>
                                {row.plain && <div>{row.plain}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {b.foot && <p className="cfri-footnote">{b.foot}</p>}
        </div>
    );
}
